import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../api/client';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  LinearProgress,
  Menu,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Queue as QueueIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

// Типы данных
interface Queue {
  id: string;
  name: string;
  code: string;
  status: 'active' | 'paused' | 'closed';
  service_name?: string;
  current_number: number;
  max_tickets_per_day: number;
  priority?: number;
  workstation_ids?: string[];
  workstation_names?: string[];
  waiting_count: number;
  in_service_count: number;
  completed_today: number;
  avgServiceTimeToday: number;
  active_operators: number;
}

interface QueueFormData {
  name: string;
  code: string;
  serviceId: string;
  maxTicketsPerDay: number;
  priority: number;
  workstationIds: string[];
}

// Схема валидации
const queueValidationSchema = yup.object({
  name: yup.string()
    .min(3, 'Название должно содержать минимум 3 символа')
    .required('Название обязательно'),
  code: yup.string()
    .min(1, 'Код обязателен')
    .matches(/^[A-Z0-9]+$/, 'Код может содержать только заглавные буквы и цифры')
    .required('Код обязателен'),
  serviceId: yup.string().required('Выберите услугу'),
  maxTicketsPerDay: yup.number()
    .min(1, 'Минимум 1 талон в день')
    .max(10000, 'Максимум 10000 талонов в день')
    .required('Укажите максимальное количество талонов'),
  priority: yup.number()
    .min(1, 'Минимум 1')
    .max(10, 'Максимум 10')
    .required('Укажите приоритет'),
  workstationIds: yup.array()
    .of(yup.string())
    .min(1, 'Выберите хотя бы одну кассу')
    .required('Выберите кассы'),
});

export const QueuesPage: React.FC = () => {
  const [selectedQueue, setSelectedQueue] = useState<Queue | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'view' | 'delete'>('create');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuQueueId, setMenuQueueId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Загрузка данных
  const { data: queuesData, isLoading, error, refetch } = useQuery(['queues'], async () => {
    const res = await apiClient.get('/api/queues');
    return res.data;
  }, { refetchInterval: 15000 });

  const { data: servicesData } = useQuery(['services'], async () => {
    const res = await apiClient.get('/api/services');
    return res.data;
  });

  const { data: workstationsData } = useQuery(['workstations'], async () => {
    const res = await apiClient.get('/api/operators/workstations');
    return res.data;
  });

  // Мутации
  const createQueueMutation = useMutation(
    (queueData: QueueFormData) => apiClient.post('/api/queues', queueData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['queues']);
        setDialogOpen(false);
        setSnackbar({ open: true, message: 'Очередь успешно создана', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка создания очереди', severity: 'error' });
      },
    }
  );

  const updateQueueMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<QueueFormData> }) => 
      apiClient.put(`/api/queues/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['queues']);
        setDialogOpen(false);
        setSnackbar({ open: true, message: 'Очередь успешно обновлена', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка обновления очереди', severity: 'error' });
      },
    }
  );

  const deleteQueueMutation = useMutation(
    (id: string) => apiClient.delete(`/api/queues/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['queues']);
        setDialogOpen(false);
        setSnackbar({ open: true, message: 'Очередь успешно удалена', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка удаления очереди', severity: 'error' });
      },
    }
  );

  const updateQueueStatusMutation = useMutation(
    ({ id, status }: { id: string; status: string }) => 
      apiClient.put(`/api/queues/${id}`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['queues']);
        setSnackbar({ open: true, message: 'Статус очереди изменен', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка изменения статуса', severity: 'error' });
      },
    }
  );

  // Форма
  const formik = useFormik<QueueFormData>({
    initialValues: {
      name: '',
      code: '',
      serviceId: '',
      maxTicketsPerDay: 100,
      priority: 1,
      workstationIds: [],
    },
    validationSchema: queueValidationSchema,
    onSubmit: (values) => {
      if (dialogType === 'create') {
        createQueueMutation.mutate(values);
      } else if (dialogType === 'edit' && selectedQueue) {
        updateQueueMutation.mutate({ id: selectedQueue.id, data: values });
      }
    },
  });

  // Обработчики событий
  const handleOpenDialog = (type: 'create' | 'edit' | 'view' | 'delete', queue?: Queue) => {
    setDialogType(type);
    setSelectedQueue(queue || null);
    
    if (type === 'edit' && queue) {
      formik.setValues({
        name: queue.name,
        code: queue.code,
        serviceId: '',
        maxTicketsPerDay: queue.max_tickets_per_day,
        priority: queue.priority || 1,
        workstationIds: queue.workstation_ids || [],
      });
    } else if (type === 'create') {
      formik.resetForm();
    }
    
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedQueue(null);
    formik.resetForm();
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, queueId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuQueueId(queueId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuQueueId(null);
  };

  const handleStatusChange = (queueId: string, newStatus: string) => {
    updateQueueStatusMutation.mutate({ id: queueId, status: newStatus });
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'closed': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Активна';
      case 'paused': return 'Приостановлена';
      case 'closed': return 'Закрыта';
      default: return status;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Управление очередями</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Ошибка загрузки очередей</Alert>
      </Box>
    );
  }

  const queues: Queue[] = queuesData?.data?.queues || [];

  // Статистика
  const totalQueues = queues.length;
  const activeQueues = queues.filter(q => q.status === 'active').length;
  const totalWaiting = queues.reduce((sum, q) => sum + q.waiting_count, 0);
  const totalCompleted = queues.reduce((sum, q) => sum + q.completed_today, 0);

  // Данные для графиков
  const queuePerformanceData = queues.map(queue => ({
    name: queue.name,
    waiting: queue.waiting_count,
    completed: queue.completed_today,
    avgTime: queue.avgServiceTimeToday,
  }));

  return (
    <>
      <Helmet>
        <title>Управление очередями - ELEKTROM</title>
      </Helmet>

      <Box>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Управление очередями
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Создание, настройка и мониторинг очередей обслуживания
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Обновить данные">
              <IconButton onClick={() => refetch()} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Создать очередь
            </Button>
          </Box>
        </Box>

        {/* Статистические карточки */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Всего очередей</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{totalQueues}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Активных: {activeQueues}
                    </Typography>
                  </Box>
                  <QueueIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>В ожидании</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{totalWaiting}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>клиентов</Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Обслужено сегодня</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{totalCompleted}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>талонов</Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Среднее время</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>
                      {Math.round(queues.reduce((sum, q) => sum + q.avgServiceTimeToday, 0) / queues.length) || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>минут</Typography>
                  </Box>
                  <TimerIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* График производительности */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Производительность очередей
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={queuePerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="waiting" fill="#ff9800" name="В ожидании" />
                <Bar dataKey="completed" fill="#4caf50" name="Обслужено" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Таблица очередей */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Список очередей
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Очередь</TableCell>
                    <TableCell align="center">Приоритет</TableCell>
                    <TableCell align="center">Статус</TableCell>
                    <TableCell align="center">Кассы</TableCell>
                    <TableCell align="center">В ожидании</TableCell>
                    <TableCell align="center">Обслуживается</TableCell>
                    <TableCell align="center">Завершено сегодня</TableCell>
                    <TableCell align="center">Среднее время</TableCell>
                    <TableCell align="center">Операторы</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queues.map((queue) => (
                    <TableRow key={queue.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {queue.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {queue.code} • {queue.service_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={queue.priority || 1}
                          color={queue.priority <= 3 ? 'error' : queue.priority <= 6 ? 'warning' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusText(queue.status)}
                          color={getStatusColor(queue.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                          {(queue.workstation_names || []).slice(0, 2).map((name: string, index: number) => (
                            <Chip
                              key={index}
                              label={name}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                          {(queue.workstation_names || []).length > 2 && (
                            <Chip
                              label={`+${(queue.workstation_names || []).length - 2}`}
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" color="warning.main">
                          {queue.waiting_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" color="info.main">
                          {queue.in_service_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" color="success.main">
                          {queue.completed_today}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {queue.avgServiceTimeToday} мин
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={queue.active_operators}
                          color={queue.active_operators > 0 ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Просмотр">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('view', queue)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Редактировать">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('edit', queue)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Дополнительно">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, queue.id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Контекстное меню */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => menuQueueId && handleStatusChange(menuQueueId, 'active')}
            disabled={queues.find(q => q.id === menuQueueId)?.status === 'active'}
          >
            <PlayIcon color="success" sx={{ mr: 1 }} />
            Запустить
          </MenuItem>
          <MenuItem
            onClick={() => menuQueueId && handleStatusChange(menuQueueId, 'paused')}
            disabled={queues.find(q => q.id === menuQueueId)?.status === 'paused'}
          >
            <PauseIcon color="warning" sx={{ mr: 1 }} />
            Приостановить
          </MenuItem>
          <MenuItem
            onClick={() => menuQueueId && handleStatusChange(menuQueueId, 'closed')}
            disabled={queues.find(q => q.id === menuQueueId)?.status === 'closed'}
          >
            <StopIcon color="error" sx={{ mr: 1 }} />
            Закрыть
          </MenuItem>
          <MenuItem
            onClick={() => {
              const queue = queues.find(q => q.id === menuQueueId);
              if (queue) handleOpenDialog('delete', queue);
            }}
          >
            <DeleteIcon color="error" sx={{ mr: 1 }} />
            Удалить
          </MenuItem>
        </Menu>

        {/* Диалог создания/редактирования */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {dialogType === 'create' && 'Создание новой очереди'}
            {dialogType === 'edit' && 'Редактирование очереди'}
            {dialogType === 'view' && 'Информация об очереди'}
            {dialogType === 'delete' && 'Подтверждение удаления'}
          </DialogTitle>
          
          <form onSubmit={formik.handleSubmit}>
            <DialogContent dividers>
              {dialogType === 'delete' ? (
                <Alert severity="warning">
                  Вы действительно хотите удалить очередь "{selectedQueue?.name}"?
                  Это действие нельзя отменить.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Название очереди"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      disabled={dialogType === 'view'}
                      placeholder="Например: Паспортный стол"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Код очереди"
                      name="code"
                      value={formik.values.code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.code && Boolean(formik.errors.code)}
                      helperText={formik.touched.code && formik.errors.code}
                      disabled={dialogType === 'view'}
                      placeholder="Например: PS01"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={formik.touched.serviceId && Boolean(formik.errors.serviceId)}>
                      <InputLabel>Услуга</InputLabel>
                      <Select
                        name="serviceId"
                        value={formik.values.serviceId}
                        onChange={formik.handleChange}
                        disabled={dialogType === 'view'}
                        label="Услуга"
                      >
                        {servicesData?.data?.services?.map((service: any) => (
                          <MenuItem key={service.id} value={service.id}>
                            {service.name} ({service.estimated_duration} мин)
                          </MenuItem>
                        )) || [
                          <MenuItem key="1" value="1">Консультации (15 мин)</MenuItem>,
                          <MenuItem key="2" value="2">Оформление документов (30 мин)</MenuItem>,
                          <MenuItem key="3" value="3">Платежи (10 мин)</MenuItem>
                        ]}
                      </Select>
                      {formik.touched.serviceId && formik.errors.serviceId && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {formik.errors.serviceId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Максимум талонов в день"
                      name="maxTicketsPerDay"
                      value={formik.values.maxTicketsPerDay}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.maxTicketsPerDay && Boolean(formik.errors.maxTicketsPerDay)}
                      helperText={formik.touched.maxTicketsPerDay && formik.errors.maxTicketsPerDay}
                      disabled={dialogType === 'view'}
                      inputProps={{ min: 1, max: 10000 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Приоритет (1-10)"
                      name="priority"
                      value={formik.values.priority}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.priority && Boolean(formik.errors.priority)}
                      helperText={formik.touched.priority && formik.errors.priority}
                      disabled={dialogType === 'view'}
                      inputProps={{ min: 1, max: 10 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth error={formik.touched.workstationIds && Boolean(formik.errors.workstationIds)}>
                      <InputLabel>Кассы</InputLabel>
                      <Select
                        name="workstationIds"
                        multiple
                        value={formik.values.workstationIds}
                        onChange={(e) => formik.setFieldValue('workstationIds', e.target.value)}
                        disabled={dialogType === 'view'}
                        label="Кассы"
                        renderValue={(selected) => {
                          const selectedWorkstations = (selected as string[]).map(id => 
                            workstationsData?.data?.workstations?.find((w: any) => w.id === id)?.name || id
                          );
                          return selectedWorkstations.join(', ');
                        }}
                      >
                        {workstationsData?.data?.workstations?.map((workstation: any) => (
                          <MenuItem key={workstation.id} value={workstation.id}>
                            {workstation.name} (Окно {workstation.window_number})
                          </MenuItem>
                        )) || []}
                      </Select>
                      {formik.touched.workstationIds && formik.errors.workstationIds && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {formik.errors.workstationIds}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  
                  {dialogType === 'view' && selectedQueue && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          Статистика
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" color="warning.main">
                            {selectedQueue.waiting_count}
                          </Typography>
                          <Typography variant="body2">В ожидании</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" color="info.main">
                            {selectedQueue.in_service_count}
                          </Typography>
                          <Typography variant="body2">Обслуживается</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">
                            {selectedQueue.completed_today}
                          </Typography>
                          <Typography variant="body2">Завершено сегодня</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                {dialogType === 'view' ? 'Закрыть' : 'Отмена'}
              </Button>
              {dialogType === 'delete' && (
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => selectedQueue && deleteQueueMutation.mutate(selectedQueue.id)}
                  disabled={deleteQueueMutation.isLoading}
                >
                  {deleteQueueMutation.isLoading ? 'Удаление...' : 'Удалить'}
                </Button>
              )}
              {(dialogType === 'create' || dialogType === 'edit') && (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={createQueueMutation.isLoading || updateQueueMutation.isLoading}
                >
                  {createQueueMutation.isLoading || updateQueueMutation.isLoading 
                    ? 'Сохранение...' 
                    : dialogType === 'create' ? 'Создать очередь' : 'Сохранить изменения'
                  }
                </Button>
              )}
            </DialogActions>
          </form>
        </Dialog>
        
        {/* Уведомления */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};
